// @flow

import React from 'react';
import styled from 'styled-components';
import { Carousel, Link } from 'components/Gui';
import type { CarouselConsumerProps } from 'components/Gui';
import { absolute, Color, flex, hover, Transition, Width, topMargin } from 'styles';
import { RightChevronArrow } from 'components/Svg';
import type { AllImages } from 'hooks/use-images';
import { labelToId } from 'utils';
import { BlockyText } from 'components/Content';

const Wrapper = styled.div`
  ${({ marginTop }) => marginTop && topMargin(...marginTop)};
  position: relative;
  z-index: -1;
`;

const CarouselWrapper = styled.div`
  width: 100%;
  max-width: 415px;
  height: 45rem;
  flex: 0 0 auto;
  position: relative;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    height: 38rem;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    height: 40rem;
    width: 100%;
    max-width: unset;
  }
`;

const Slide = styled.div`
  ${absolute()};
  ${flex()};
  transform: translateX(${({ positionX }) => `${positionX * 100}%`});
  transition: ${({ opacity }) =>
    opacity ? `transform 500ms ${Transition.EASE_MOBILE}, opacity 500ms ${Transition.EASE_MOBILE}` : `none`};
  opacity: ${({ opacity }) => opacity};
  pointer-events: ${({ opacity }) => (opacity === 1 ? 'auto' : 'none')};

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    z-index: 1;
    transform: ${({ positionX }) => {
      if (positionX === -1) return `translateX(${positionX * 18}%) scale(0.8)`;
      if (positionX === 3) return `translateX(110%) scale(0.7)`;
      return `translateX(${positionX * 50}%)`;
    }};
    ${({ positionX }) => [-1, 3].indexOf(positionX) !== -1 && 'z-index: 0'};
    opacity: ${({ positionX }) => {
      if (positionX === 2) return 0.4;
      if ([0, 1].indexOf(positionX) !== -1) return 1;
      return 0;
    }};
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    display: block;
  }

  > div {
    pointer-events: auto;
    width: calc(100% - 20px);
    height: 100%;
    background: white;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 40px;

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      width: calc(50% - 16px);
    }
  }
`;

const Buttons = styled.div`
  ${absolute(0, '-10px', 0, '-10px')};
  ${flex('space-between', 'center')};
  pointer-events: none;
  z-index: 1;

  @media print, screen and (min-width: 580px) {
    right: -10vw;
    left: -10vw;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    right: -40px;
    left: -40px;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL + 100}px) {
    right: -70px;
    left: -70px;
  }

  > button {
    height: 40px;
    width: 40px;
    border-radius: 50%;
    ${flex()};
    ${hover('background', Color.YELLOW_MELLOW, Color.PURPLE_FIGURE, true)};
    pointer-events: auto;

    &:first-of-type {
      transform: rotate(180deg);

      @media print, screen and (min-width: ${Width.BREAK_XL}px) {
        opacity: 0;
        pointer-events: none;
      }
    }

    svg {
      width: 8px;
      transform: translateX(1px);
      * {
        stroke: white;
      }
    }
  }
`;

type Props = {
  marginTop?: number[],
  label: string,
  slides: any[],
  images: AllImages,
};

const BlockyTrustpilotTestimonialCarousel = ({ marginTop, label, slides, images, ...rest }: Props) => {
  const id = labelToId(label);
  return (
    <Wrapper marginTop={marginTop} {...rest}>
      <Carousel data={slides} label={label}>
        {({ changeSlide, currentIndex, slideContent, slideOpacity, slidePositionX, onScreen }: CarouselConsumerProps) => (
          <CarouselWrapper aria-live="polite" id={id}>
            {slideContent.map(({ text, name }, index) => (
              <Slide
                aria-hidden={slidePositionX[index] !== 0}
                aria-roledescription="slide"
                role="group"
                aria-label={`Testiominal of ${name}`}
                key={`BlockyTrustpilotTestimonialCarouselSlide-${index}`}
                current={currentIndex === index}
                opacity={slideOpacity[index]}
                positionX={slidePositionX[index]}>
                <div>
                  <BlockyText alignContent={['center', 'left', 'left']} body={{ content: text }} />
                </div>
              </Slide>
            ))}
            <Buttons>
              <Link aria-controls={id} label={`Previous Slide`} onClick={() => changeSlide()}>
                {RightChevronArrow}
              </Link>
              <Link aria-controls={id} label={`Next Slide`} onClick={() => changeSlide(true)}>
                {RightChevronArrow}
              </Link>
            </Buttons>
          </CarouselWrapper>
        )}
      </Carousel>
    </Wrapper>
  );
};

BlockyTrustpilotTestimonialCarousel.contentType = 'BlockyTrustpilotTestimonialCarousel';

export default BlockyTrustpilotTestimonialCarousel;
